import { evalMath, formatMathNumber, type AngleMode } from "./mathEval";
import { convertUnits, getCategory, UNIT_CATEGORIES } from "./unitConvert";
import {
  DEFAULT_GRAPH_RANGE,
  GRAPH_SERIES_COLORS,
  zoomGraphRange,
  type GraphRange,
} from "./graphPlot";

export type { GraphRange };

export type CalculatorMode = "calc" | "graph" | "convert";

export type CalculatorVariant = "calc" | "graph_units";

export const ALL_CALCULATOR_MODES: CalculatorMode[] = ["calc", "graph", "convert"];

export const CALCULATOR_VARIANT: Record<
  CalculatorVariant,
  { enabledModes: CalculatorMode[]; mode: CalculatorMode; title: string }
> = {
  calc: {
    enabledModes: ["calc"],
    mode: "calc",
    title: "Калькулятор",
  },
  graph_units: {
    enabledModes: ["graph"],
    mode: "graph",
    title: "Графики",
  },
};

export type GraphSeriesSpec = {
  id: string;
  expr: string;
  domain: string;
  color: string;
  /** Показывать ли кривую на графике. */
  visible: boolean;
};

export type GraphPointSpec = {
  id: string;
  x: number;
  y: number;
  filled: boolean;
};

export const MAX_GRAPH_SERIES = 5;
export const MAX_GRAPH_POINTS = 12;

export function newGraphSeriesId(): string {
  return `s-${Math.random().toString(36).slice(2, 8)}`;
}

export function newGraphPointId(): string {
  return `p-${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultGraphSeries(): GraphSeriesSpec[] {
  return [
    {
      id: "s1",
      expr: "x",
      domain: "",
      color: GRAPH_SERIES_COLORS[0] ?? "#6246ea",
      visible: true,
    },
  ];
}

export type CalculatorState = {
  mode: CalculatorMode;
  /** Какие вкладки доступны; для старых объектов — все три. */
  enabledModes: CalculatorMode[];
  /** Набираемое выражение (видно знаки). */
  expr: string;
  /** Результат после = или текущий ввод-дисплей. */
  display: string;
  history: string[];
  angleMode: AngleMode;
  /** Режим 2ⁿᵈ (как в Windows) — локально на объекте, для синхронизации UI. */
  secondMode: boolean;
  /** Ячейки памяти (MS/M+/M−), сверху — последняя. Как список в Windows. */
  memory: number[];
  /** F-E: показывать результат в экспоненциальной записи. */
  fe: boolean;
  graphExpr: string;
  graphRange: GraphRange;
  graphSeries: GraphSeriesSpec[];
  graphPoints: GraphPointSpec[];
  convertCategory: string;
  convertFrom: string;
  convertTo: string;
  convertValue: string;
};

export const CALCULATOR_DEFAULT_STATE: CalculatorState = {
  mode: "calc",
  enabledModes: [...ALL_CALCULATOR_MODES],
  expr: "",
  display: "0",
  history: [],
  angleMode: "deg",
  secondMode: false,
  memory: [],
  fe: false,
  graphExpr: "x",
  graphRange: { ...DEFAULT_GRAPH_RANGE },
  graphSeries: defaultGraphSeries(),
  graphPoints: [],
  convertCategory: "length",
  convertFrom: "m",
  convertTo: "km",
  convertValue: "1",
};

function normalizeEnabledModes(raw: unknown): CalculatorMode[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: CalculatorMode[] = [];
  for (const m of raw) {
    if (m === "calc" || m === "graph" || m === "convert") {
      if (!out.includes(m)) out.push(m);
    }
  }
  return out.length ? out : null;
}

export function clampCalculatorMode(
  mode: CalculatorMode,
  enabledModes: CalculatorMode[]
): CalculatorMode {
  if (enabledModes.includes(mode)) return mode;
  return enabledModes[0] ?? "calc";
}

export function calculatorTitle(enabledModes: CalculatorMode[]): string {
  const hasCalc = enabledModes.includes("calc");
  const hasGraph = enabledModes.includes("graph");
  const hasConvert = enabledModes.includes("convert");
  if (hasCalc && !hasGraph && !hasConvert) return "Инженерный";
  if (!hasCalc && hasGraph && !hasConvert) return "Графики";
  if (!hasCalc && (hasGraph || hasConvert)) return "Графики и единицы";
  return "Калькулятор";
}

export type CalculatorKey =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "."
  | "+"
  | "-"
  | "*"
  | "/"
  | "^"
  | "("
  | ")"
  | "="
  | "C"
  | "BKSP"
  | "±"
  | "%"
  | "pi"
  | "e"
  | "sin"
  | "cos"
  | "tan"
  | "cot"
  | "asin"
  | "acos"
  | "atan"
  | "acot"
  | "sqrt"
  | "sqr"
  | "cube"
  | "cbrt"
  | "inv"
  | "abs"
  | "fact"
  | "pow10"
  | "pow2"
  | "exp"
  | "log"
  | "ln"
  | "mod"
  | "x"
  | "DEG"
  | "RAD"
  | "2nd"
  // Тригонометрия целиком, как во всплывающем меню Windows
  | "sec"
  | "csc"
  | "asec"
  | "acsc"
  | "sinh"
  | "cosh"
  | "tanh"
  | "coth"
  | "sech"
  | "csch"
  | "asinh"
  | "acosh"
  | "atanh"
  | "acoth"
  | "asech"
  | "acsch"
  // Меню «Функция»
  | "floor"
  | "ceil"
  | "rand"
  | "dms"
  | "degs"
  | "FE";

const MAX_EXPR = 120;
/** Журнал — как в Windows: список, а не три последних строки. */
const MAX_HISTORY = 20;
/** Сколько ячеек памяти храним (столько же принимает сервер). */
const MAX_MEMORY = 20;

/** Константы и скобки — просто дописываются. */
const INSERT_CHUNK: Partial<Record<CalculatorKey, string>> = {
  pi: "pi",
  e: "e",
  x: "x",
  "(": "(",
  ")": ")",
};

const BINARY_OPS = new Set(["+", "-", "*", "/", "^"]);

/** Функции: сначала число, потом кнопка → применяется к последнему слагаемому. */
const WRAP_FN: Partial<Record<CalculatorKey, string>> = {
  sin: "sin",
  cos: "cos",
  tan: "tan",
  cot: "cot",
  sec: "sec",
  csc: "csc",
  asin: "asin",
  acos: "acos",
  atan: "atan",
  acot: "acot",
  asec: "asec",
  acsc: "acsc",
  sinh: "sinh",
  cosh: "cosh",
  tanh: "tanh",
  coth: "coth",
  sech: "sech",
  csch: "csch",
  asinh: "asinh",
  acosh: "acosh",
  atanh: "atanh",
  acoth: "acoth",
  asech: "asech",
  acsch: "acsch",
  floor: "floor",
  ceil: "ceil",
  dms: "dms",
  degs: "degs",
  sqrt: "sqrt",
  cbrt: "cbrt",
  abs: "abs",
  log: "log",
  ln: "ln",
};

function pushHistory(history: string[], entry: string): string[] {
  const next = [entry, ...history.filter((h) => h !== entry)];
  return next.slice(0, MAX_HISTORY);
}

function resetAfterError(state: CalculatorState): CalculatorState {
  return {
    ...CALCULATOR_DEFAULT_STATE,
    mode: state.mode,
    enabledModes: state.enabledModes,
    angleMode: state.angleMode,
    secondMode: false,
    memory: state.memory,
    fe: state.fe,
    history: state.history,
    graphExpr: state.graphExpr,
    graphRange: state.graphRange,
    graphSeries: state.graphSeries,
    graphPoints: state.graphPoints,
    convertCategory: state.convertCategory,
    convertFrom: state.convertFrom,
    convertTo: state.convertTo,
    convertValue: state.convertValue,
  };
}

function setExpr(state: CalculatorState, expr: string): CalculatorState {
  if (expr.length > MAX_EXPR) return state;
  return { ...state, expr, display: expr || "0" };
}

function appendExpr(state: CalculatorState, chunk: string): CalculatorState {
  if (state.display === "Error") {
    return appendExpr(resetAfterError(state), chunk);
  }
  let expr = state.expr;
  // After "=", start fresh unless chunk is an operator / )
  if (state.expr === "" && state.display !== "0" && /^[+\-*/^)]/.test(chunk)) {
    expr = state.display;
  }
  if (expr.length + chunk.length > MAX_EXPR) return state;
  expr = expr + chunk;
  return { ...state, expr, display: expr || "0" };
}

/** Бинарный оператор: ошибочный знак заменяется следующим (25+ затем − → 25−). */
function appendBinaryOp(state: CalculatorState, op: string): CalculatorState {
  if (state.display === "Error") {
    return appendBinaryOp(resetAfterError(state), op);
  }
  let expr = state.expr;
  if (expr === "" && state.display !== "0") {
    expr = state.display;
  }
  while (expr.length > 0 && BINARY_OPS.has(expr[expr.length - 1]!)) {
    expr = expr.slice(0, -1);
  }
  // Снимаем хвостовой infix mod (10mod → 10+)
  if (/mod$/i.test(expr)) {
    expr = expr.slice(0, -3);
  }
  if (expr.length + op.length > MAX_EXPR) return state;
  expr = expr + op;
  return { ...state, expr, display: expr || "0", secondMode: false };
}

function appendModOp(state: CalculatorState): CalculatorState {
  if (state.display === "Error") {
    return appendModOp(resetAfterError(state));
  }
  let expr = state.expr;
  if (expr === "" && state.display !== "0") {
    expr = state.display;
  }
  while (expr.length > 0 && BINARY_OPS.has(expr[expr.length - 1]!)) {
    expr = expr.slice(0, -1);
  }
  if (/mod$/i.test(expr)) {
    expr = expr.slice(0, -3);
  }
  if (expr.length + 3 > MAX_EXPR) return state;
  expr = expr + "mod";
  return { ...state, expr, display: expr || "0", secondMode: false };
}

function currentInner(state: CalculatorState): string {
  if (state.expr.trim()) return state.expr.trim();
  if (state.display === "Error") return "";
  // После "=" берём результат с дисплея; пустой «0» не оборачиваем — тогда вставим sin(
  if (state.display !== "0") return state.display;
  return "";
}

/** Бинарный «−», а не унарный минус у числа. */
function isBinaryOpAt(expr: string, i: number): boolean {
  const c = expr[i];
  if (c === "+" || c === "*" || c === "/" || c === "^") return true;
  if (c !== "-") return false;
  if (i === 0) return false;
  const prev = expr[i - 1]!;
  if (prev === "(" || BINARY_OPS.has(prev)) return false;
  return true;
}

/**
 * Начало последнего операнда: 25+25 → 25; (25+25) целиком; 25+sin(30) → sin(30).
 * Чтобы корень/sin шли только на последнее слагаемое; всё выражение — через свои скобки.
 */
function findLastOperandStart(expr: string): number {
  let depth = 0;
  for (let i = expr.length - 1; i >= 0; i--) {
    const c = expr[i]!;
    if (c === ")") depth++;
    else if (c === "(") {
      depth--;
      if (depth < 0) return 0;
    } else if (depth === 0 && isBinaryOpAt(expr, i)) {
      return i + 1;
    }
  }
  return 0;
}

/** Унарная операция только на последнее слагаемое / операнд. */
function applyUnaryOnLast(
  state: CalculatorState,
  wrap: (last: string) => string,
  openIfEmpty?: string,
): CalculatorState {
  const full = currentInner(state);
  if (!full) {
    return openIfEmpty ? appendExpr(state, openIfEmpty) : state;
  }
  const start = findLastOperandStart(full);
  const head = full.slice(0, start);
  const last = full.slice(start);
  if (!last || BINARY_OPS.has(last[last.length - 1]!) || last.endsWith("(")) {
    return openIfEmpty ? setExpr(state, full + openIfEmpty) : state;
  }
  return setExpr(state, head + wrap(last));
}

/** Число, которое сейчас на экране: по нему работают MS, M+, M−. */
function currentNumber(state: CalculatorState): number {
  const raw = state.expr.trim() || state.display;
  if (!raw || raw === "Error") return NaN;
  const n = evalMath(raw, { angleMode: state.angleMode });
  return Number.isFinite(n) ? n : NaN;
}

/** Подставить число вместо последнего операнда — так работает MR. */
function replaceLastOperand(state: CalculatorState, text: string): CalculatorState {
  const full = currentInner(state);
  if (!full) return setExpr(state, text);
  const start = findLastOperandStart(full);
  const head = full.slice(0, start);
  // Отрицательное число после знака берём в скобки: 5+(−3), а не 5+−3.
  const body = head && text.startsWith("-") ? `(${text})` : text;
  return setExpr(state, head + body);
}

/** Убрать хвостовые знаки операций и mod, чтобы добраться до последнего числа. */
function stripTrailingOps(expr: string): string {
  let s = expr;
  for (;;) {
    if (s.length && BINARY_OPS.has(s[s.length - 1]!)) {
      s = s.slice(0, -1);
      continue;
    }
    if (s.length >= 3 && s.slice(-3).toLowerCase() === "mod") {
      s = s.slice(0, -3);
      continue;
    }
    return s;
  }
}

/**
 * Две строки экрана, как в Windows: сверху ход вычисления, снизу — число.
 *
 * Выражение у нас хранится целиком, поэтому «текущее число» — это его
 * последний операнд: 25+7 → сверху «25+», крупно «7».
 */
export function splitCalcDisplay(state: CalculatorState): { top: string; main: string } {
  if (state.display === "Error") return { top: "", main: "Ошибка" };
  const expr = state.expr;
  if (!expr) {
    const last = state.history[0];
    const cut = last ? last.lastIndexOf("=") : -1;
    return {
      top: cut > 0 ? `${last!.slice(0, cut)} =` : "",
      main: state.display,
    };
  }
  const start = findLastOperandStart(expr);
  const main = expr.slice(start);
  if (main && main.slice(-3).toLowerCase() !== "mod") {
    return { top: expr.slice(0, start), main };
  }
  // Хвост — знак операции: наверху всё выражение, крупно предыдущее число.
  const stripped = stripTrailingOps(expr);
  const prev = stripped.slice(findLastOperandStart(stripped));
  return { top: expr, main: prev || "0" };
}

export type MemoryOp = "store" | "recall" | "add" | "sub" | "clearAll" | "clearAt";

/**
 * Память как в Windows: MS кладёт новую ячейку наверх, M+/M− меняют верхнюю
 * (или выбранную из списка), MR подставляет её в выражение, MC очищает всё.
 */
export function applyMemoryOp(
  state: CalculatorState,
  op: MemoryOp,
  index = 0
): CalculatorState {
  if (op === "clearAll") return { ...state, memory: [] };

  const mem = [...state.memory];
  if (op === "clearAt") {
    if (index < 0 || index >= mem.length) return state;
    mem.splice(index, 1);
    return { ...state, memory: mem };
  }

  if (op === "recall") {
    const v = mem[index];
    if (v === undefined) return state;
    const text = formatMathNumber(v);
    if (text === "Error") return state;
    return replaceLastOperand(state, text);
  }

  const v = currentNumber(state);
  if (!Number.isFinite(v)) return state;

  if (op === "store") {
    mem.unshift(v);
    return { ...state, memory: mem.slice(0, MAX_MEMORY) };
  }

  const base = mem[index];
  if (base === undefined) {
    // Пустая память: M+ заводит первую ячейку, M− — её же со знаком минус.
    mem.unshift(op === "add" ? v : -v);
    return { ...state, memory: mem.slice(0, MAX_MEMORY) };
  }
  mem[index] = op === "add" ? base + v : base - v;
  return { ...state, memory: mem };
}

/** F-E: то же число в экспоненциальной записи. Выражение не трогаем. */
export function formatDisplayFE(display: string, fe: boolean): string {
  if (!fe) return display;
  // Переводим только готовое число: набранное выражение оставляем как есть.
  if (!display.trim()) return display;
  const n = Number(display);
  if (!Number.isFinite(n) || n === 0) return display;
  return n.toExponential();
}

/**
 * Кнопки, у которых есть «вторая» роль. После них 2ⁿᵈ гаснет (как в Windows),
 * а на цифрах и знаках остаётся включённым.
 */
const SECOND_SHIFT_KEYS = new Set<CalculatorKey>([
  "sqr", "cube", "sqrt", "cbrt", "pow10", "pow2", "log", "ln", "exp",
  "sin", "cos", "tan", "cot", "sec", "csc",
  "asin", "acos", "atan", "acot", "asec", "acsc",
  "sinh", "cosh", "tanh", "coth", "sech", "csch",
  "asinh", "acosh", "atanh", "acoth", "asech", "acsch",
]);

export function applyCalculatorKey(state: CalculatorState, key: CalculatorKey): CalculatorState {
  const next = applyCalculatorKeyInner(state, key);
  if (key === "2nd" || !state.secondMode) return next;
  return { ...next, secondMode: !SECOND_SHIFT_KEYS.has(key) };
}

function applyCalculatorKeyInner(
  state: CalculatorState,
  key: CalculatorKey
): CalculatorState {
  if (key === "2nd") {
    return { ...state, secondMode: !state.secondMode };
  }

  if (key === "DEG") return { ...state, angleMode: "deg", secondMode: false };
  if (key === "RAD") return { ...state, angleMode: "rad", secondMode: false };

  if (key === "C") {
    return {
      ...state,
      expr: "",
      display: "0",
      secondMode: false,
    };
  }

  if (key === "BKSP") {
    if (!state.expr) {
      return { ...state, display: "0", secondMode: false };
    }
    let expr = state.expr;
    if (/mod$/i.test(expr)) expr = expr.slice(0, -3);
    else expr = expr.slice(0, -1);
    return { ...state, expr, display: expr || "0", secondMode: false };
  }

  if (key === "=") {
    const raw = state.expr.trim() || state.display;
    if (!raw || raw === "0") return { ...state, secondMode: false };
    const result = evalMath(raw, { angleMode: state.angleMode });
    const formatted = formatMathNumber(result);
    if (formatted === "Error") {
      return { ...state, display: "Error", expr: "", secondMode: false };
    }
    const entry = `${raw}=${formatted}`;
    return {
      ...state,
      expr: "",
      display: formatted,
      history: pushHistory(state.history, entry),
      secondMode: false,
    };
  }

  if (key === "±") {
    return { ...applyUnaryOnLast(state, (last) => `-(${last})`), secondMode: false };
  }

  if (key === "%") {
    return { ...applyUnaryOnLast(state, (last) => `(${last})/100`), secondMode: false };
  }

  if (key === "sqr") {
    return { ...applyUnaryOnLast(state, (last) => `(${last})^2`), secondMode: false };
  }

  if (key === "cube") {
    return { ...applyUnaryOnLast(state, (last) => `(${last})^3`), secondMode: false };
  }

  if (key === "cbrt") {
    return {
      ...applyUnaryOnLast(state, (last) => `cbrt(${last})`, "cbrt("),
      secondMode: false,
    };
  }

  if (key === "inv") {
    return { ...applyUnaryOnLast(state, (last) => `1/(${last})`), secondMode: false };
  }

  if (key === "fact") {
    return { ...applyUnaryOnLast(state, (last) => `(${last})!`), secondMode: false };
  }

  if (key === "pow10") {
    return { ...applyUnaryOnLast(state, (last) => `10^(${last})`), secondMode: false };
  }

  if (key === "pow2") {
    return { ...applyUnaryOnLast(state, (last) => `2^(${last})`), secondMode: false };
  }

  if (key === "exp") {
    return {
      ...applyUnaryOnLast(state, (last) => `exp(${last})`, "exp("),
      secondMode: false,
    };
  }

  if (key === "FE") {
    return { ...state, fe: !state.fe };
  }

  if (key === "rand") {
    // Как в Windows: кнопка подставляет случайное число от 0 до 1.
    // Восемь знаков после запятой: длинное число не влезает в строку.
    const value = String(Number(Math.random().toFixed(8)));
    return { ...replaceLastOperand(state, value), secondMode: false };
  }

  if (key === "mod") {
    return appendModOp(state);
  }

  const wrapName = WRAP_FN[key];
  if (wrapName) {
    return {
      ...applyUnaryOnLast(state, (last) => `${wrapName}(${last})`, `${wrapName}(`),
      secondMode: false,
    };
  }

  if (key === "+" || key === "-" || key === "*" || key === "/" || key === "^") {
    return appendBinaryOp(state, key);
  }

  const insert = INSERT_CHUNK[key];
  if (insert) {
    return { ...appendExpr(state, insert), secondMode: false };
  }

  if (key === "." || key === "0" || key === "1" || key === "2" || key === "3" || key === "4" || key === "5" || key === "6" || key === "7" || key === "8" || key === "9") {
    return { ...appendExpr(state, key), secondMode: false };
  }

  return state;
}

export type CalculatorAction =
  | { type: "key"; key: CalculatorKey }
  | { type: "memory"; op: MemoryOp; index?: number }
  | { type: "clearHistory" }
  | { type: "useHistory"; index: number }
  | { type: "setMode"; mode: CalculatorMode }
  | { type: "setGraphExpr"; graphExpr: string }
  | { type: "setGraphSeries"; graphSeries: GraphSeriesSpec[] }
  | { type: "setGraphPoints"; graphPoints: GraphPointSpec[] }
  | { type: "graphZoom"; factor: number }
  | { type: "graphSetRange"; graphRange: GraphRange }
  | { type: "graphReset" }
  | { type: "setConvertCategory"; category: string }
  | { type: "setConvertFrom"; unit: string }
  | { type: "setConvertTo"; unit: string }
  | { type: "setConvertValue"; value: string }
  | { type: "swapConvert" };

function sanitizeGraphSeries(raw: unknown): GraphSeriesSpec[] {
  if (!Array.isArray(raw)) return defaultGraphSeries();
  const out: GraphSeriesSpec[] = [];
  for (const item of raw) {
    if (out.length >= MAX_GRAPH_SERIES) break;
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const expr = typeof o.expr === "string" ? o.expr.slice(0, MAX_EXPR) : "";
    const domain = typeof o.domain === "string" ? o.domain.slice(0, 80) : "";
    const color =
      typeof o.color === "string" && /^#[0-9a-fA-F]{6}$/.test(o.color)
        ? o.color
        : GRAPH_SERIES_COLORS[out.length % GRAPH_SERIES_COLORS.length]!;
    const id = typeof o.id === "string" && o.id.trim() ? o.id.slice(0, 24) : newGraphSeriesId();
    const visible = o.visible !== false;
    out.push({ id, expr, domain, color, visible });
  }
  return out.length ? out : defaultGraphSeries();
}

function sanitizeGraphRange(raw: unknown): GraphRange | null {
  if (!raw || typeof raw !== "object") return null;
  const gr = raw as Record<string, unknown>;
  const nums = ["xMin", "xMax", "yMin", "yMax"] as const;
  if (!nums.every((k) => typeof gr[k] === "number" && Number.isFinite(gr[k]))) return null;
  const xMin = gr.xMin as number;
  const xMax = gr.xMax as number;
  const yMin = gr.yMin as number;
  const yMax = gr.yMax as number;
  if (!(xMax > xMin) || !(yMax > yMin)) return null;
  return { xMin, xMax, yMin, yMax };
}

function sanitizeGraphPoints(raw: unknown): GraphPointSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: GraphPointSpec[] = [];
  for (const item of raw) {
    if (out.length >= MAX_GRAPH_POINTS) break;
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const x = typeof o.x === "number" && Number.isFinite(o.x) ? o.x : Number(o.x);
    const y = typeof o.y === "number" && Number.isFinite(o.y) ? o.y : Number(o.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const id = typeof o.id === "string" && o.id.trim() ? o.id.slice(0, 24) : newGraphPointId();
    out.push({ id, x, y, filled: o.filled !== false });
  }
  return out;
}

export function applyCalculatorAction(
  state: CalculatorState,
  action: CalculatorAction
): CalculatorState {
  switch (action.type) {
    case "key":
      return applyCalculatorKey(state, action.key);
    case "memory":
      return applyMemoryOp(state, action.op, action.index ?? 0);
    case "clearHistory":
      return state.history.length ? { ...state, history: [] } : state;
    case "useHistory": {
      const entry = state.history[action.index];
      if (!entry) return state;
      const value = entry.slice(entry.lastIndexOf("=") + 1).trim();
      return value ? replaceLastOperand(state, value) : state;
    }
    case "setMode":
      return {
        ...state,
        mode: clampCalculatorMode(action.mode, state.enabledModes),
      };
    case "setGraphExpr": {
      const graphExpr = action.graphExpr.slice(0, MAX_EXPR);
      const series = state.graphSeries.length
        ? state.graphSeries.map((s, i) => (i === 0 ? { ...s, expr: graphExpr } : s))
        : [
            {
              id: "s1",
              expr: graphExpr,
              domain: "",
              color: GRAPH_SERIES_COLORS[0]!,
              visible: true,
            },
          ];
      return { ...state, graphExpr, graphSeries: series };
    }
    case "setGraphSeries": {
      const graphSeries = sanitizeGraphSeries(action.graphSeries);
      return {
        ...state,
        graphSeries,
        graphExpr: graphSeries[0]?.expr ?? "",
      };
    }
    case "setGraphPoints":
      return { ...state, graphPoints: sanitizeGraphPoints(action.graphPoints) };
    case "graphZoom":
      return {
        ...state,
        graphRange: zoomGraphRange(state.graphRange, action.factor),
      };
    case "graphSetRange": {
      const graphRange = sanitizeGraphRange(action.graphRange);
      return graphRange ? { ...state, graphRange } : state;
    }
    case "graphReset":
      return { ...state, graphRange: { ...DEFAULT_GRAPH_RANGE } };
    case "setConvertCategory": {
      const cat = getCategory(action.category) ?? UNIT_CATEGORIES[0]!;
      return {
        ...state,
        convertCategory: cat.id,
        convertFrom: cat.units[0]!.id,
        convertTo: cat.units[1]?.id ?? cat.units[0]!.id,
      };
    }
    case "setConvertFrom":
      return { ...state, convertFrom: action.unit };
    case "setConvertTo":
      return { ...state, convertTo: action.unit };
    case "setConvertValue":
      return {
        ...state,
        convertValue: action.value.replace(/[^\d.eE+-]/g, "").slice(0, 24) || "0",
      };
    case "swapConvert":
      return {
        ...state,
        convertFrom: state.convertTo,
        convertTo: state.convertFrom,
      };
    default:
      return state;
  }
}

export function convertResult(state: CalculatorState, valueOverride?: string): string {
  const raw = (valueOverride ?? state.convertValue).trim().replace(",", ".");
  if (!raw || raw === "-" || raw === "." || raw === "-.") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "—";
  const out = convertUnits(n, state.convertCategory, state.convertFrom, state.convertTo);
  return formatMathNumber(out, 18);
}

export function readCalculatorState(obj: Record<string, unknown> | object): CalculatorState {
  const o = obj as Record<string, unknown>;
  const enabledModes = normalizeEnabledModes(o.enabledModes) ?? [...ALL_CALCULATOR_MODES];
  const rawMode =
    o.mode === "graph" || o.mode === "convert" || o.mode === "calc" ? o.mode : "calc";
  const mode = clampCalculatorMode(rawMode, enabledModes);
  const expr = typeof o.expr === "string" ? o.expr : "";
  let display =
    typeof o.display === "string" && o.display.length > 0 ? o.display : "0";
  // Legacy four-function objects without expr: show display only
  if (!("expr" in o) && typeof o.display === "string") {
    display = o.display;
  }
  const history = Array.isArray(o.history)
    ? o.history.filter((h): h is string => typeof h === "string").slice(0, MAX_HISTORY)
    : [];
  const angleMode: AngleMode = o.angleMode === "rad" ? "rad" : "deg";
  const secondMode = o.secondMode === true;
  const memory = Array.isArray(o.memory)
    ? o.memory
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
        .slice(0, MAX_MEMORY)
    : [];
  const fe = o.fe === true;
  const graphExpr = typeof o.graphExpr === "string" ? o.graphExpr : "x";
  let graphRange = { ...DEFAULT_GRAPH_RANGE };
  if (o.graphRange && typeof o.graphRange === "object") {
    const gr = o.graphRange as Record<string, unknown>;
    const nums = ["xMin", "xMax", "yMin", "yMax"] as const;
    if (nums.every((k) => typeof gr[k] === "number" && Number.isFinite(gr[k]))) {
      graphRange = {
        xMin: gr.xMin as number,
        xMax: gr.xMax as number,
        yMin: gr.yMin as number,
        yMax: gr.yMax as number,
      };
    }
  }
  let graphSeries = sanitizeGraphSeries(o.graphSeries);
  if (!Array.isArray(o.graphSeries) && graphExpr) {
    graphSeries = [
      {
        id: "s1",
        expr: graphExpr.slice(0, MAX_EXPR),
        domain: "",
        color: GRAPH_SERIES_COLORS[0] ?? "#6246ea",
        visible: true,
      },
    ];
  }
  const graphPoints = sanitizeGraphPoints(o.graphPoints);
  const convertCategory =
    typeof o.convertCategory === "string" ? o.convertCategory : "length";
  const cat = getCategory(convertCategory) ?? UNIT_CATEGORIES[0]!;
  const convertFrom =
    typeof o.convertFrom === "string" ? o.convertFrom : cat.units[0]!.id;
  const convertTo =
    typeof o.convertTo === "string"
      ? o.convertTo
      : cat.units[1]?.id ?? cat.units[0]!.id;
  const convertValue =
    typeof o.convertValue === "string" && o.convertValue.length > 0
      ? o.convertValue
      : "1";

  return {
    mode,
    enabledModes,
    expr,
    display,
    history,
    angleMode,
    secondMode,
    memory,
    fe,
    graphExpr: graphSeries[0]?.expr ?? graphExpr,
    graphRange,
    graphSeries,
    graphPoints,
    convertCategory: cat.id,
    convertFrom,
    convertTo,
    convertValue,
  };
}

export function calculatorStateToPatch(state: CalculatorState): Record<string, unknown> {
  return {
    mode: state.mode,
    // enabledModes задаётся при создании и не меняется действиями UI
    expr: state.expr,
    display: state.display,
    history: state.history,
    angleMode: state.angleMode,
    secondMode: state.secondMode,
    memory: state.memory,
    fe: state.fe,
    graphExpr: state.graphExpr,
    graphRange: state.graphRange,
    graphSeries: state.graphSeries,
    graphPoints: state.graphPoints,
    convertCategory: state.convertCategory,
    convertFrom: state.convertFrom,
    convertTo: state.convertTo,
    convertValue: state.convertValue,
    // clear legacy fields if present
    acc: null,
    pendingOp: null,
    overwrite: true,
  };
}
